import React, { useEffect, useMemo, useRef, useState } from "react";

import { rest, t } from "../services";
import { loadingIcon } from "../loadingIcon";
import { DELETE_ATTACHMENT_EVENT, FILE_UPLOADED_EVENT } from "../events/Gui";
import { FileComponent } from "../widgets/FileComponent";
import { getServiceUri } from "../metadata/MetadataLoader";

const FILE_TYPE_ICONS = {
  jpg: "file-image",
  gif: "file-image",
  png: "file-image",
  bmp: "file-image",
  mov: "file-video",
  wav: "file-audio",
  mp3: "file-audio",
  xls: "file-excel",
  xlsx: "file-excel",
  doc: "file-word",
  docx: "file-word",
  ppt: "file-powerpoint",
  pptx: "file-powerpoint",
  csv: "file-csv",
  pdf: "file-pdf",
};

const getFileExt = /(?:\.([^.]+))?$/;

const arrayRemove = (arr, id) => {
  return (arr || []).filter((element) => element.id !== id);
};

export const Attachment = (props) => {
  const ext = getFileExt.exec(props.attachment.fileName)?.[1]?.toLowerCase();
  const icon = (ext && FILE_TYPE_ICONS[ext]) || "file";

  return (
    <FileComponent
      data={props.attachment}
      readOnly={props.readOnly}
      onDownload={() => props.handleClick(props.attachment.id)}
      onDelete={() => props.handleDelete(props.attachment.id)}
      previewUrl={() => props.previewUrl(props.attachment.id)}
      token={props.token}
      updateFileData={props.updateFileData}
      icon={icon}
    />
  );
};

const AttachmentList = (props) => {
  const items = useMemo(() => {
    return (props.list || []).map((element) => (
      <Attachment key={element.id} {...props} attachment={element} />
    ));
  }, [props]);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: 4,
      }}
    >
      {items}
    </div>
  );
};

export const AttachmentsArea = (props) => {
  const [data, setData] = useState(undefined);
  const [token, setToken] = useState(undefined);

  const mountedRef = useRef(true);
  const subscriptionsSetRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;

    async function init() {
      let tok = token;

      if (!tok) {
        tok = await rest.getToken(getServiceUri() + "token/get-auth-code");

        if (!mountedRef.current) {
          return;
        }

        setToken(tok);
      }

      const response = await props.loadData();

      if (!mountedRef.current) {
        return;
      }

      setData(Array.isArray(response) ? response : []);
    }

    if (data === undefined) {
      init();
    }

    return () => {
      mountedRef.current = false;
    };
  }, [data, token, props]);

  useEffect(() => {
    if (subscriptionsSetRef.current) {
      return undefined;
    }

    subscriptionsSetRef.current = true;

    const onUpload = (event) => {
      setData((prev) => {
        const current = Array.isArray(prev) ? prev : [];
        return [...current, event];
      });
    };

    const onDelete = (id) => {
      setData((prev) => {
        if (!Array.isArray(prev)) {
          return prev;
        }

        return arrayRemove(prev, id);
      });
    };

    FILE_UPLOADED_EVENT.subscribe(onUpload);
    DELETE_ATTACHMENT_EVENT.subscribe(onDelete);

    return () => {
      subscriptionsSetRef.current = false;

      if (typeof FILE_UPLOADED_EVENT.unsubscribe === "function") {
        FILE_UPLOADED_EVENT.unsubscribe(onUpload);
      }

      if (typeof DELETE_ATTACHMENT_EVENT.unsubscribe === "function") {
        DELETE_ATTACHMENT_EVENT.unsubscribe(onDelete);
      }
    };
  }, []);

  if (data === undefined) {
    return loadingIcon;
  }

  return (
    <>
      <label style={{ marginTop: "20px" }}>{t(props.label)}</label>
      <AttachmentList {...props} list={data} setData={setData} token={token} />
    </>
  );
};